import React, { useEffect, useState } from "react";
import {
  getAllHybridBonusWithdrawals,
  getPendingHybridBonusWithdrawals,
  approveHybridBonusWithdrawals,
  approveHybridBonusWithdrawalsManually,
  rejectHybridBonusWithdrawals,
} from "../../../services/Admin";
import formatDate from "../../../utils/formatDate";
import Loader from "../../components/loader/Loader";
import Loader2 from "../../components/loader/Loader2";
import CustomTable from "../../components/custom-table/CustomTable";
import CustomTableCheckbox from "../../components/custom-table/CustomTableCheckbox";
import { toast } from "react-toastify";
import "./style.css";

function AllHybridBonusWithdrawals() {
  const [isLoading, setIsLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [pendingList, setPendingList] = useState([]);
  const [historyList, setHistoryList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [activeTab, setActiveTab] = useState("request");
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    totalUSDT: 0,
    totalRetopup: 0,
    totalInr: 0,
    cgtFailures: 0,
  });

  const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "-");

  const pendingColumns = [
    { Header: "S.no", accessor: "sno" },
    { Header: "User ID", accessor: "userId" },
    { Header: "Name", accessor: "userName" },
    { Header: "Email", accessor: "userEmail" },
    { Header: "CGT Homes Email", accessor: "cgtHomesEmail" },
    { Header: "USDT", accessor: "usdtAmount" },
    { Header: "Retopup", accessor: "retopupAmount" },
    { Header: "CGT Homes INR", accessor: "cgtHomesInr" },
    { Header: "Wallet Address", accessor: "walletAddress" },
    { Header: "Account Number", accessor: "accountNumber" },
    { Header: "IFSC Code", accessor: "ifscCode" },
    { Header: "Date", accessor: "date" },
  ];

  const historyColumns = [
    { Header: "S.no", accessor: "sno" },
    { Header: "User ID", accessor: "userId" },
    { Header: "Name", accessor: "userName" },
    { Header: "Email", accessor: "userEmail" },
    { Header: "CGT Homes Email", accessor: "cgtHomesEmail" },
    { Header: "USDT Paid", accessor: "usdtAmount" },
    { Header: "Retopup", accessor: "retopupAmount" },
    { Header: "CGT Homes INR", accessor: "cgtHomesInr" },
    { Header: "CGT Status", accessor: "cgtStatus" },
    { Header: "Date", accessor: "date" },
    { Header: "Wallet", accessor: "wallet" },
    { Header: "Txn Hash", accessor: "txHash" },
  ];

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const [historyRes, pendingRes] = await Promise.all([
        getAllHybridBonusWithdrawals(),
        getPendingHybridBonusWithdrawals(),
      ]);

      const historyData = historyRes?.data || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let todayCount = 0;
      let totalUSDT = 0;
      let totalRetopup = 0;
      let totalInr = 0;
      let cgtFailures = 0;

      historyData.forEach((r) => {
        totalUSDT += Number(r.usdtAmount) || 0;
        totalRetopup += Number(r.retopupAmount) || 0;
        totalInr += Number(r.cgtHomesInrAmount) || 0;
        if (r.cgtHomesStatus && r.cgtHomesStatus !== "Completed") cgtFailures++;
        const d = new Date(r.createdAt);
        d.setHours(0, 0, 0, 0);
        if (d.getTime() === today.getTime()) todayCount++;
      });

      const formattedHistory = historyData.map((r, i) => {
        const cgtOk = r.cgtHomesStatus === "Completed";
        const cgtColor = cgtOk ? "#10b981" : r.cgtHomesStatus ? "#ef4444" : "#6b7280";
        return {
          sno: i + 1,
          userId: r.userId || "-",
          userName: r.userName || "Unknown",
          userEmail: r.userEmail || "-",
          cgtHomesEmail: r.cgtHomesEmail || "-",
          usdtAmount: `$${(Number(r.usdtAmount) || 0).toFixed(2)}`,
          retopupAmount: `$${(Number(r.retopupAmount) || 0).toFixed(2)}`,
          cgtHomesInr: `₹${(Number(r.cgtHomesInrAmount) || 0).toLocaleString("en-IN")}`,
          cgtStatus: (
            <span style={{ color: cgtColor, fontWeight: 500 }}>
              {r.cgtHomesStatus || "—"}
              {r.cgtHomesLegged === false && (
                <span style={{ color: "#9ca3af", fontWeight: 400, marginLeft: 6 }}>
                  (legacy)
                </span>
              )}
            </span>
          ),
          date: r.createdAt ? formatDate(r.createdAt) : "-",
          wallet: r.toAddress ? (
            <a
              href={`https://bscscan.com/address/${r.toAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              title={r.toAddress}
            >
              {shortAddr(r.toAddress)}
            </a>
          ) : (
            "-"
          ),
          txHash: r.txHash ? (
            <a
              href={`https://bscscan.com/tx/${r.txHash}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              BSC Scan
            </a>
          ) : (
            "-"
          ),
        };
      });

      const pendingData = pendingRes?.requests || [];
      const formattedPending = pendingData.map((r, i) => ({
        sno: i + 1,
        _id: r._id,
        userId: r.userId || "-",
        userName: r.userName || "Unknown",
        userEmail: r.userEmail || "-",
        cgtHomesEmail: r.connectedCGTHomesEmail || r.metadata?.destinationEmail || "-",
        usdtAmount: `$${(Number(r.metadata?.usdtAmount) || 0).toFixed(2)}`,
        retopupAmount: `$${(Number(r.metadata?.retopupAmount) || 0).toFixed(2)}`,
        cgtHomesInr: `₹${(Number(r.metadata?.cgtHomesInrAmount) || 0).toLocaleString("en-IN")}`,
        walletAddress: r.walletAddress || "-",
        accountNumber: r.accountNumber || "-",
        ifscCode: r.ifscCode || "-",
        date: r.createdAt ? formatDate(r.createdAt) : "-",
      }));

      setHistoryList(formattedHistory);
      setPendingList(formattedPending);
      setStats({
        total: historyData.length,
        today: todayCount,
        totalUSDT,
        totalRetopup,
        totalInr,
        cgtFailures,
      });
    } catch (err) {
      console.error("Error fetching 100-days withdrawals:", err);
      toast.error("Failed to load 100-days withdrawals");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const onSelectedRows = (selectedFlatRows) => {
    setSelectedRows(selectedFlatRows);
  };

  const handleApprove = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Approve ${selectedRows.length} hybrid bonus withdrawal(s)?`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await approveHybridBonusWithdrawals(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} approved`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error approving withdrawals");
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveManually = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Approve ${selectedRows.length} hybrid bonus withdrawal(s) manually through bank account? No USDT will be sent on-chain.`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await approveHybridBonusWithdrawalsManually(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} approved manually`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error approving withdrawals manually");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Reject ${selectedRows.length} hybrid bonus withdrawal(s)? The package will be unlocked so the user can retry.`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await rejectHybridBonusWithdrawals(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} rejected`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error rejecting withdrawals");
    } finally {
      setProcessing(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="investment-history">
      {processing && <Loader2 />}

      <h1 className="main-heading">100 Days Withdrawals (Hybrid Bonus)</h1>
      <br />

      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Total Withdrawals</p>
            <h2 className="stat-value">{stats.total}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Today</p>
            <h2 className="stat-value">{stats.today}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">USDT Paid</p>
            <h2 className="stat-value">${stats.totalUSDT.toFixed(2)}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Retopup Credited</p>
            <h2 className="stat-value">${stats.totalRetopup.toFixed(2)}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">CGT Homes INR</p>
            <h2 className="stat-value">
              ₹{stats.totalInr.toLocaleString("en-IN")}
            </h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">CGT Homes Failures</p>
            <h2 className="stat-value">{stats.cgtFailures}</h2>
          </div>
        </div>
      </div>

      <br />

      <div className="hbw-tabs">
        <button
          className={`hbw-tab${activeTab === "request" ? " active" : ""}`}
          onClick={() => setActiveTab("request")}
          type="button"
        >
          Request ({pendingList.length})
        </button>
        <button
          className={`hbw-tab${activeTab === "history" ? " active" : ""}`}
          onClick={() => setActiveTab("history")}
          type="button"
        >
          History ({historyList.length})
        </button>
      </div>

      {activeTab === "request" ? (
        <>
          <div className="button-group" style={{ margin: "1rem 0" }}>
            <button
              onClick={handleReject}
              disabled={processing || !selectedRows.length}
              style={{ background: "red", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Reject Selected
            </button>
            <button
              onClick={handleApproveManually}
              disabled={processing || !selectedRows.length}
              style={{ background: "#2563eb", marginLeft: "0.5rem", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Approve Manually
            </button>
            <button
              onClick={handleApprove}
              disabled={processing || !selectedRows.length}
              style={{ background: "green", marginLeft: "0.5rem", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Approve Selected
            </button>
          </div>

          <CustomTableCheckbox
            columns={pendingColumns}
            data={pendingList}
            heading={`Pending 100 Days Withdrawals (${pendingList.length})`}
            onSelectedRows={onSelectedRows}
          />
        </>
      ) : (
        <CustomTable
          data={historyList}
          columns={historyColumns}
          heading={`100 Days Withdrawals History (${historyList.length})`}
        />
      )}
    </div>
  );
}

export default AllHybridBonusWithdrawals;
